import React, { useState, useEffect } from 'react';
import { fetchUnfinishedOrdersAP } from '../services/api';
import DateRangeFilter from '../components/DateRangeFilter';
import { toTitleCase } from '../utils/format';

const DASH = String.fromCharCode(8212);
const ARROW_L = String.fromCharCode(8592);
const ARROW_R = String.fromCharCode(8594);
const todayIso = new Date().toISOString().split('T')[0];
const PAGE_SIZE = 50;

function toApiDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y}`;
}

export default function UnfinishedOrdersAP() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [start, setStart] = useState(`${new Date().getFullYear()}-01-01`);
  const [end, setEnd] = useState(todayIso);
  const [page, setPage] = useState(1);

  const load = () => {
    setLoading(true);
    setError(null);
    fetchUnfinishedOrdersAP({ StartDate: toApiDate(start), EndDate: toApiDate(end), PageNumber: page, PageSize: PAGE_SIZE })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.data ?? [];
  const total = data?.totalRows ?? rows.length;
  const compCount = rows.filter(r => r.sendCompEmail).length;

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Unfinished Orders (AP)</h2>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 2 }}>Abandoned online checkouts by new customers who never completed an order (admin-parity)</div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-header">Order Date Range</div>
        <div style={{ padding: '12px 16px', display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <DateRangeFilter start={start} end={end} onChange={(s, e) => { setStart(s); setEnd(e); }} />
          <button onClick={() => { setPage(1); load(); }} className="btn btn-primary" style={{ fontSize: 11 }}>Search</button>
        </div>
      </div>

      {rows.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 8, marginBottom: 12 }}>
          {[
            { label: 'Unfinished Orders', value: total.toLocaleString() },
            { label: 'Flagged for Comp Email', value: compCount.toLocaleString(), accent: true },
          ].map(({ label, value, accent }) => (
            <div key={label} className="card" style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
              <div style={{ fontSize: 20, fontWeight: 800, color: accent ? 'var(--orange)' : 'var(--navy)' }}>{value}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="card-header">Abandoned Checkouts {total > 0 && `(${total.toLocaleString()})`}</div>
        {loading && <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>Loading...</div>}
        {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red)' }}>Error: {error}</div>}
        {!loading && !error && rows.length === 0 && (
          <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)' }}>No unfinished orders in this range.</div>
        )}
        {rows.length > 0 && (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', fontSize: 11.5, borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                    <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order ID</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Customer</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Email</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Ship Method</th>
                    <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Referring Ad</th>
                    <th style={{ textAlign: 'center', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Comp Email</th>
                    <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order Time</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={row.orderId ?? i} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
                      <td style={{ padding: '8px 12px', color: 'var(--orange)', fontWeight: 600 }}>{(row.bcOrderId && String(row.bcOrderId) !== '0' ? row.bcOrderId : row.orderId) ?? DASH}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--navy)' }}>{toTitleCase([row.firstName, row.lastName].filter(Boolean).join(' ') || DASH)}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-700)', maxWidth: 200 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.email ?? DASH}</div>
                      </td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)' }}>{(row.shipMethod || DASH).trim()}</td>
                      <td style={{ padding: '8px 8px', color: 'var(--gray-500)', maxWidth: 160 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.referingAd || DASH}</div>
                      </td>
                      <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                        {row.sendCompEmail
                          ? <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10, background: '#fef3f0', color: '#c0392b' }}>Yes</span>
                          : <span style={{ color: 'var(--gray-300)' }}>{DASH}</span>}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>
                        {row.oTime ? new Date(row.oTime).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : DASH}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {total > PAGE_SIZE && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 12 }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="btn btn-navy" style={{ fontSize: 11 }}>{ARROW_L} Prev</button>
                <span style={{ fontSize: 11, alignSelf: 'center' }}>Page {page} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}</span>
                <button onClick={() => setPage(p => p + 1)} disabled={rows.length < PAGE_SIZE} className="btn btn-navy" style={{ fontSize: 11 }}>Next {ARROW_R}</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
